import pptxgen from 'pptxgenjs'
import { moduleById } from '../data/skillModules'
import { AudienceLevel, LEVEL_LABELS } from '../types/pathway'
import { formatHours, totalHours } from './pathwayMatching'

interface PowerPointInput {
  pathwayName: string
  company: string
  audience: AudienceLevel | 'any'
  items: string[]
}

const COLORS = {
  navy: '30718D',
  coral: 'DB3725',
  ink: '111827',
  slate: '6B7280',
  mist: 'F3F4F6',
  white: 'FFFFFF',
}

/** Builds a native, editable PowerPoint deck (.pptx). */
export async function downloadPathwayPowerPoint({
  pathwayName,
  company,
  audience,
  items,
}: PowerPointInput): Promise<void> {
  const deck = new pptxgen()
  deck.layout = 'LAYOUT_WIDE'
  deck.author = 'University of Phoenix Professional Development'
  deck.company = 'University of Phoenix'
  deck.subject = 'Recommended Learning Pathway'
  deck.title = pathwayName.trim() || 'Recommended Learning Pathway'
  deck.lang = 'en-US'
  deck.theme = {
    headFontFace: 'Aptos Display',
    bodyFontFace: 'Aptos',
    lang: 'en-US',
  }

  const title = pathwayName.trim() || 'Recommended Learning Pathway'
  const recipient = company.trim() || 'your team'
  const audienceLabel =
    audience === 'any' ? 'Mixed audience' : LEVEL_LABELS[audience]
  const modules = items.map(moduleById).filter(Boolean)

  const cover = deck.addSlide()
  cover.background = { color: COLORS.navy }
  cover.addText('UNIVERSITY OF PHOENIX · PROFESSIONAL DEVELOPMENT', {
    x: 0.75,
    y: 0.72,
    w: 8,
    h: 0.24,
    fontFace: 'Aptos',
    fontSize: 10,
    bold: true,
    charSpacing: 1.4,
    color: 'CFE3EC',
    margin: 0,
  })
  cover.addText(title, {
    x: 0.75,
    y: 2.15,
    w: 8.75,
    h: 1.3,
    fontFace: 'Aptos Display',
    fontSize: 32,
    bold: true,
    color: COLORS.white,
    breakLine: false,
    margin: 0,
    valign: 'mid',
  })
  cover.addShape('line', {
    x: 0.75,
    y: 3.82,
    w: 1.05,
    h: 0,
    line: { color: COLORS.coral, width: 3 },
  })
  cover.addText(`Prepared for ${recipient} · ${audienceLabel}`, {
    x: 0.75,
    y: 4.1,
    w: 7.8,
    h: 0.3,
    fontSize: 16,
    color: 'E5F0F5',
    margin: 0,
  })
  cover.addText(`${modules.length} modules · ${formatHours(totalHours(items))} total`, {
    x: 0.75,
    y: 4.55,
    w: 5,
    h: 0.3,
    fontSize: 13,
    color: 'CFE3EC',
    margin: 0,
  })

  const overview = deck.addSlide()
  addHeader(overview, 'THE PATHWAY', 'Learning in the recommended order')
  modules.forEach((module, index) => {
    const column = index % 3
    const row = Math.floor(index / 3)
    const x = 0.8 + column * 4.15
    const y = 2 + row * 2.15
    overview.addShape('line', {
      x,
      y,
      w: 0,
      h: 1.35,
      line: { color: COLORS.coral, width: 3 },
    })
    overview.addText(`0${index + 1}`, {
      x: x + 0.18,
      y,
      w: 0.4,
      h: 0.22,
      fontSize: 10,
      bold: true,
      color: COLORS.coral,
      margin: 0,
    })
    overview.addText(module!.name, {
      x: x + 0.18,
      y: y + 0.35,
      w: 3.55,
      h: 0.72,
      fontSize: 16,
      bold: true,
      color: COLORS.ink,
      margin: 0,
      breakLine: false,
      fit: 'shrink',
    })
  })

  modules.forEach((module, index) => {
    const slide = deck.addSlide()
    addHeader(slide, `MODULE ${index + 1} OF ${modules.length}`, module!.name)
    slide.addText(module!.category, {
      x: 0.8,
      y: 1.63,
      w: 4.5,
      h: 0.24,
      fontSize: 12,
      color: COLORS.slate,
      margin: 0,
    })
    slide.addText('Description', {
      x: 0.8,
      y: 2.25,
      w: 4.9,
      h: 0.26,
      fontSize: 14,
      bold: true,
      color: COLORS.navy,
      margin: 0,
    })
    slide.addText(module!.description, {
      x: 0.8,
      y: 2.6,
      w: 5.15,
      h: 2.55,
      fontSize: 14,
      color: COLORS.ink,
      breakLine: false,
      margin: 0,
      valign: 'top',
      fit: 'shrink',
    })
    slide.addShape('line', {
      x: 6.55,
      y: 2.25,
      w: 0,
      h: 3.35,
      line: { color: 'D1D5DB', width: 1 },
    })
    slide.addText('Expected outcomes', {
      x: 7.05,
      y: 2.25,
      w: 4.7,
      h: 0.26,
      fontSize: 14,
      bold: true,
      color: COLORS.navy,
      margin: 0,
    })
    slide.addText(
      module!.outcomes.map((outcome) => ({ text: outcome, options: { bullet: { indent: 14 }, hanging: 3 } })),
      {
        x: 7.05,
        y: 2.65,
        w: 4.65,
        h: 2.85,
        fontSize: 14,
        color: COLORS.ink,
        breakLine: false,
        margin: 0,
        valign: 'top',
        fit: 'shrink',
        paraSpaceAfterPt: 10,
      },
    )
  })

  await deck.writeFile({ fileName: powerpointFileName(pathwayName) })
}

export function powerpointFileName(pathwayName: string): string {
  const safe = (pathwayName.trim() || 'learning-pathway')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
  return `${safe}.pptx`
}

type DeckSlide = ReturnType<pptxgen['addSlide']>

function addHeader(slide: DeckSlide, eyebrow: string, heading: string) {
  slide.background = { color: COLORS.white }
  slide.addShape('rect', {
    x: 0,
    y: 0,
    w: 13.333,
    h: 0.12,
    fill: { color: COLORS.coral },
    line: { color: COLORS.coral },
  })
  slide.addText(eyebrow, {
    x: 0.8,
    y: 0.65,
    w: 4.5,
    h: 0.24,
    fontSize: 10,
    bold: true,
    charSpacing: 1.4,
    color: COLORS.navy,
    margin: 0,
  })
  slide.addText(heading, {
    x: 0.8,
    y: 1,
    w: 11.7,
    h: 0.48,
    fontFace: 'Aptos Display',
    fontSize: 26,
    bold: true,
    color: COLORS.ink,
    margin: 0,
    fit: 'shrink',
  })
}
